import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Plus, Trash, MapPin, PencilSimple } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const EMPTY = { name: "", address: "", phone: "", is_hub: true, company_id: "" };

export default function Branches() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/branches");
    setRows(data);
    if (user.role === "super_admin" && companies.length === 0) {
      const c = await api.get("/companies");
      setCompanies(c.data);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    try {
      if (!form.name?.trim()) { toast.error("Branch name is required"); return; }
      if (editing) {
        const payload = { name: form.name, address: form.address, phone: form.phone, is_hub: form.is_hub };
        await api.patch(`/branches/${editing.id}`, payload);
        toast.success("Branch updated");
      } else {
        const payload = { ...form };
        if (user.role !== "super_admin") delete payload.company_id;
        else if (!payload.company_id) { toast.error("Please select a company"); return; }
        await api.post("/branches", payload);
        toast.success("Branch created");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const edit = (b) => {
    setEditing(b);
    setForm({ name: b.name, address: b.address || "", phone: b.phone || "", is_hub: !!b.is_hub, company_id: b.company_id });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!confirm("Delete this branch?")) return;
    try {
      await api.delete(`/branches/${id}`);
      toast.success("Branch deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || "—";

  const canManage = user.role === "super_admin" || user.role === "company_admin";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="branches-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Network</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            Branches & Hubs
          </h1>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="add-branch-btn">
                <Plus size={16} className="mr-1.5" weight="bold" /> New Branch
              </Button>
            </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
              data-testid="branch-dialog"
            >
              <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
                <DialogTitle>{editing ? "Edit Branch" : "Create Branch"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto px-5 sm:px-6 py-4 flex-1" data-testid="branch-dialog-body">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Branch name</Label>
                  <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Address</Label>
                  <div className="mt-1.5">
                    <AddressAutocomplete
                      value={form.address}
                      onChange={(v) => setForm({ ...form, address: v })}
                      dataTestId="branch-address"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Phone</Label>
                  <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="branch-phone" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="branch-is-hub-wrap">
                  <input
                    type="checkbox"
                    checked={form.is_hub}
                    onChange={(e) => setForm({ ...form, is_hub: e.target.checked })}
                    data-testid="branch-is-hub"
                    className="rounded border-zinc-300"
                  />
                  <span className="text-sm text-zinc-700">Use as scanning hub on package trail</span>
                </label>
                {user.role === "super_admin" && !editing && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="branch-company"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-branch-btn">
                  {editing ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="branches-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Branch</th>
              <th className="px-5 py-3 font-semibold">Address</th>
              <th className="px-5 py-3 font-semibold">Phone</th>
              <th className="px-5 py-3 font-semibold">Type</th>
              {user.role === "super_admin" && <th className="px-5 py-3 font-semibold">Company</th>}
              {canManage && <th className="px-5 py-3 font-semibold text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-500">
                <MapPin size={28} className="mx-auto mb-2 text-zinc-300" />
                No branches yet. Create one to track package hub scans.
              </td></tr>
            ) : rows.map((b) => (
              <tr key={b.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`branch-row-${b.id}`}>
                <td className="px-5 py-4 font-medium text-zinc-900">{b.name}</td>
                <td className="px-5 py-4 text-zinc-700 text-xs">{b.address || "—"}</td>
                <td className="px-5 py-4 text-zinc-700 text-xs">{b.phone || "—"}</td>
                <td className="px-5 py-4">
                  {b.is_hub ? (
                    <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-[#002BF6]/10 text-[#002BF6] border border-[#002BF6]/20" data-testid={`branch-hub-${b.id}`}>
                      Hub
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200">
                      Branch
                    </span>
                  )}
                </td>
                {user.role === "super_admin" && <td className="px-5 py-4 text-zinc-700 text-xs">{companyName(b.company_id)}</td>}
                {canManage && (
                  <td className="px-5 py-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => edit(b)} data-testid={`edit-branch-${b.id}`}>
                      <PencilSimple size={15} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(b.id)} data-testid={`delete-branch-${b.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash size={15} />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
